import React from "react";
import { ShieldCheck, Search } from "lucide-react";

/** 3-tier provenance badge.
 *
 *  - "verified"  → GREEN. Byte-confirmed on the official source. Shows host + date.
 *  - "found"     → NEUTRAL (no colour). Located on a real page but not byte-confirmed —
 *                  "confirm on official site".
 *  - anything else / "not_found" → NEUTRAL. "Not verified" (the caller usually pairs
 *                  this with an explore link + an editable field).
 */
function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function VerifiedBadge({ status, source, checkedAt, label = "", compact = false }) {
  // "verified" must carry a real official source — a verified status with no source
  // is downgraded so a badge can never claim "verified" without something to point at.
  const isVerified = status === "verified" && !!source;
  const host = source ? hostOf(source) : "";
  const date = fmtDate(checkedAt);
  const what = label || "Verified";

  if (isVerified) {
    const tip = `${what}${host ? ` on ${host}` : ""}${date ? ` — ${date}` : ""} (byte-confirmed on the official source)`;
    return (
      <span className="vidhya-verified-badge ok" data-testid="verified-badge" title={tip}>
        <ShieldCheck size={12} strokeWidth={2.4} />
        <span>{what}{host ? ` · ${host}` : ""}{!compact && date ? ` · ${date}` : ""}</span>
      </span>
    );
  }

  const found = status === "found";
  const text = found ? "Found — confirm on official site" : "Not verified";
  return (
    <span
      className="vidhya-verified-badge neutral"
      data-testid={found ? "found-badge" : "unverified-badge"}
      title={found
        ? "We located this on a real page but couldn't byte-confirm it. Confirm on the official site before relying on it."
        : "We couldn't ground this against a live official page."}
      style={{
        background: "var(--paper-2, #f3f1e8)",
        color: "var(--muted, #6b6a64)",
        border: "1px solid var(--rule, #d9d6c8)",
      }}
    >
      <Search size={11} strokeWidth={2.2} />
      <span>{found && host ? `Found · ${host}` : text}</span>
    </span>
  );
}
